import { __rest } from 'tslib';
import 'core-js-pure/actual/array/find-last';
import 'core-js-pure/actual/array/find-last-index';
import '../services/base/baseDiscoverService.js';
import '../services/base/baseV3Service.js';
import '../services/sgtService.js';
import { sendAnalyticsEvent } from '../services/analyticsService.js';
import 'axios';
import '../services/base/internalApiBaseService.js';
import '../services/base/casaBaseService.js';
import '../services/scanRequestsService.js';
import '../services/base/analyticsV2BaseService.js';
import '../services/db/insightsReportsDB.js';
import { setItem, SESSION_STORAGE, getItem, removeItem } from '@analytics/storage-utils';
import { hasCookies, getCookie, setCookie } from './cookieUtils.js';
import { pruneEmpty } from './pruneEmpty.js';

const COOKIE_SUPPORTED_KEYS = ["__anon_id", "__user_id", "__community_custom_data"];
const COOKIE_EXPIRATION_TIME = 3.156e7;
const hasCookiesSupport = hasCookies();
const analyticsPlugin = (_a) => {
    var { enabled = true } = _a, analyticsConfig = __rest(_a, ["enabled"]);
    return {
        name: "peek-analytics",
        config: Object.assign(Object.assign({}, analyticsConfig), { enabled }),
        bootstrap: ({ payload, config, instance }) => {
            // Do whatever on `bootstrap` event
            const sessionId = instance.getState("context.sessionId");
            if (sessionId) {
                setItem("peek__session_id", sessionId, { storage: SESSION_STORAGE });
            }
        },
        initialize: ({ payload, config, instance, abort }) => {
            if (!config.enabled) {
                return abort("Cancel the initialize call because peek disabled");
            }
        },
        page: ({ payload, config, instance, abort }) => {
            if (!config.enabled) {
                return abort("Cancel the page call because peek disabled");
            }
            const { meta: { ts }, options } = payload, eventData = __rest(payload, ["meta", "options"]);
            const _a = getContextData(instance.getState()), { app } = _a, context = __rest(_a, ["app"]);
            const eventPayload = Object.assign(Object.assign(Object.assign(Object.assign({}, eventData), { secondsUtcTS: ts }), context), { appId: config.appId, app });
            sendAnalyticsEvent(pruneEmpty(eventPayload));
        },
        track: ({ payload, config, instance, abort }) => {
            if (!config.enabled) {
                return abort("Cancel the track call because peek disabled");
            }
            const { meta: { ts }, options } = payload, eventData = __rest(payload, ["meta", "options"]);
            const _a = getContextData(instance.getState()), { app } = _a, context = __rest(_a, ["app"]);
            const pageData = getPageData(instance.getState());
            const eventPayload = Object.assign(Object.assign(Object.assign(Object.assign({}, eventData), { secondsUtcTS: ts }), context), { page: pageData, appId: config.appId, app });
            sendAnalyticsEvent(pruneEmpty(eventPayload));
        },
        identify: ({ payload, config, instance, abort }) => {
            if (!config.enabled) {
                return abort("Cancel the track call because peek disabled");
            }
            const { meta: { ts }, options } = payload, eventData = __rest(payload, ["meta", "options"]);
            const _a = getContextData(instance.getState()), { app } = _a, context = __rest(_a, ["app"]);
            const pageData = getPageData(instance.getState());
            const { userId } = eventData, data = __rest(eventData, ["userId"]);
            const eventPayload = Object.assign(Object.assign(Object.assign(Object.assign({}, data), { secondsUtcTS: ts }), context), { page: pageData, appId: config.appId, app });
            if (["web-viewer", "welcome-site"].includes(app)) {
                eventPayload.prospectId = userId;
            }
            else {
                eventPayload.userId = userId;
            }
            sendAnalyticsEvent(pruneEmpty(eventPayload));
        },
        // ... hook into other events
    };
};
const getPageData = (analyticsState) => {
    return analyticsState.page.last.properties;
};
const getContextData = (analyticsState) => {
    const { os, app, referrer, sessionId, timezone, userAgent, version, locale } = analyticsState.context;
    return { os, app, referrer, sessionId, timezone, userAgent, version, locale };
};
const analyticsStorage = {
    getItem: (key, options) => {
        return hasCookiesSupport && COOKIE_SUPPORTED_KEYS.includes(key) ? getCookie(key) : getItem(key, options);
    },
    setItem: (key, value, options) => {
        if (hasCookiesSupport && COOKIE_SUPPORTED_KEYS.includes(key)) {
            setCookie(key, JSON.stringify(value), COOKIE_EXPIRATION_TIME, "/", ".peek.us", true, "none");
            return;
        }
        setItem(key, value, options);
    },
    removeItem: (key) => removeItem(key),
};
const getWebViewerRoomData = (analyticsState) => {
    var _a;
    return (_a = analyticsState.track.history.findLast(({ event }) => event === "Room View")) === null || _a === void 0 ? void 0 : _a.properties;
};
const isWebViewerCTAForm = (analyticsState) => {
    const lastOpenedCTAFromIndex = analyticsState.track.history.findLastIndex(({ event }) => event === "CTA_Form_Opened");
    // If no "CTA_Form_Opened" event is found, return false
    if (lastOpenedCTAFromIndex === -1) {
        return false;
    }
    // Check if "CTA_Form_Closed" occurs after the last "CTA_Form_Opened"
    return analyticsState.track.history
        .slice(lastOpenedCTAFromIndex)
        .findIndex(({ event }) => event === "CTA_Form_Closed") !== -1
        ? false
        : true;
};
const getEnrichedTrackWithRoomData = (plugin) => {
    return (eventData) => {
        const { instance, payload: { properties }, } = eventData;
        const roomData = getWebViewerRoomData(instance.getState()) || {};
        plugin.track(Object.assign(Object.assign({}, eventData), { payload: Object.assign(Object.assign({}, eventData.payload), { properties: Object.assign(Object.assign({}, roomData), properties) }) }));
    };
};

export { analyticsPlugin, analyticsStorage, analyticsPlugin as default, getContextData, getEnrichedTrackWithRoomData, getPageData, getWebViewerRoomData, isWebViewerCTAForm };
